import dayjs from "dayjs";
import "dayjs/locale/ko";

dayjs.locale("ko");

export const WEEK_DAYS = ["일", "월", "화", "수", "목", "금", "토"];

export const DATE_FORMAT = {
    date: "YYYY-MM-DD",
    datetime: "YYYY-MM-DD HH:mm:ss",
    datetimeMinute: "YYYY-MM-DD HH:mm",
    datetimeInput: "YYYY-MM-DDTHH:mm",
    datetimeSecondInput: "YYYY-MM-DDTHH:mm:ss",
    time: "HH:mm",
    dottedDate: "YYYY.MM.DD",
    dottedDatetimeMinute: "YYYY.MM.DD HH:mm",
    shortSlash: "M/D",
    monthDayKo: "M월 D일",
    dateKo: "YYYY년 M월 D일",
    dateKoWithWeekday: "YYYY년 M월 D일 (ddd)",
};

export const toDayjs = (value) => dayjs(value);

export const today = () => dayjs();

export const formatDate = (value, fallback = "") => (
    value ? dayjs(value).format(DATE_FORMAT.date) : fallback
);

export const formatDateTime = (value, fallback = "") => (
    value ? dayjs(value).format(DATE_FORMAT.datetime) : fallback
);

export const formatDateTimeMinute = (value, fallback = "") => (
    value ? dayjs(value).format(DATE_FORMAT.datetimeMinute) : fallback
);

export const formatTime = (value, fallback = "") => (
    value ? dayjs(value).format(DATE_FORMAT.time) : fallback
);

export const formatDottedDate = (value, fallback = "") => (
    value ? dayjs(value).format(DATE_FORMAT.dottedDate) : fallback
);

export const formatDottedDateTimeMinute = (value, fallback = "") => (
    value ? dayjs(value).format(DATE_FORMAT.dottedDatetimeMinute) : fallback
);

export const formatMonthDayKo = (value, fallback = "") => (
    value ? dayjs(value).format(DATE_FORMAT.monthDayKo) : fallback
);

export const formatDateKo = (value, fallback = "") => (
    value ? dayjs(value).format(DATE_FORMAT.dateKo) : fallback
);

export const formatDateKoWithWeekday = (value, fallback = "") => (
    value ? dayjs(value).format(DATE_FORMAT.dateKoWithWeekday) : fallback
);

export const formatDatetimeLocal = (value, fallback = "") => (
    value ? dayjs(value).format(DATE_FORMAT.datetimeInput) : fallback
);

export const normalizeDatetimeSeconds = (value) => {
    if (!value) return "";
    return value.length === 16 ? `${value}:00` : value;
};

export const startOfDayString = (date) => `${formatDate(date)}T00:00:00`;

export const endOfDayString = (date) => `${formatDate(date)}T23:59:59`;

export const sortByStartAt = (a, b) => dayjs(a.startAt).diff(dayjs(b.startAt));

export const getMonthCells = (year, month) => {
    const first = dayjs(new Date(year, month, 1));
    const startDow = first.day();
    const daysInMonth = first.daysInMonth();
    const cells = [];

    for (let i = startDow - 1; i >= 0; i--) {
        cells.push(first.subtract(i + 1, "day"));
    }

    for (let d = 1; d <= daysInMonth; d++) {
        cells.push(first.date(d));
    }

    while (cells.length < 42) {
        cells.push(cells[cells.length - 1].add(1, "day"));
    }

    return cells;
};

export const getWeekDays = (anchor) => {
    const start = anchor.startOf("week");
    return Array.from({ length: 7 }, (_, i) => start.add(i, "day"));
};

export const isEventOnDay = (event, day) => {
    if (!event.startAt) return false;

    const start = dayjs(event.startAt).startOf("day");
    const end = event.endAt ? dayjs(event.endAt).endOf("day") : start.endOf("day");
    const target = day.startOf("day");

    return !target.isBefore(start) && !target.isAfter(end);
};

export const formatDateTimeRange = (startAt, endAt, fallback = "") => {
    if (!startAt) return fallback;

    const start = dayjs(startAt);
    const end = endAt ? dayjs(endAt) : null;

    if (!end) return start.format(DATE_FORMAT.dottedDatetimeMinute);

    if (start.isSame(end, "day")) {
        return `${start.format(DATE_FORMAT.dottedDate)} ${start.format(DATE_FORMAT.time)} ~ ${end.format(DATE_FORMAT.time)}`;
    }

    return `${start.format(DATE_FORMAT.dottedDatetimeMinute)} ~ ${end.format(DATE_FORMAT.dottedDatetimeMinute)}`;
};

export const formatAllDayRange = (startAt, endAt, fallback = "종일") => {
    if (!startAt) return fallback;

    const start = dayjs(startAt);
    const end = endAt ? dayjs(endAt) : null;
    const endLabel = end && !start.isSame(end, "day") ? ` ~ ${end.format(DATE_FORMAT.monthDayKo)}` : "";

    return `종일 · ${start.format(DATE_FORMAT.monthDayKo)}${endLabel}`;
};

export const formatPlainDateRange = (startDate, endDate) => {
    if (!startDate) return "";
    return `${startDate}${endDate && endDate !== startDate ? ` ~ ${endDate}` : ""}`;
};

/**
 * 일정 시작 변경 시 form 상태 업데이트 (duration 유지 패턴).
 *
 * <p>시작/종료가 모두 있으면 기존 duration 을 유지한 채 종료도 함께 이동시킨다 (Google Calendar 식).
 * 한 쪽이 비어있거나 새 값이 빈 값이면 시작만 set.
 *
 * <p>사용 예:
 * <pre>
 *   onChange={e => setForm(p => applyRangeStartChange(p, e.target.value,
 *       { startKey: "startAt", endKey: "endAt" }))}
 * </pre>
 *
 * @param {object} prev - 이전 form 상태
 * @param {string} newStart - 새 시작 값 ("YYYY-MM-DD" 또는 "YYYY-MM-DDTHH:mm")
 * @param {{startKey?: string, endKey?: string, isDate?: boolean}} opts
 * @returns {object} 새 form 상태
 */
export const applyRangeStartChange = (prev, newStart, opts = {}) => {
    const { startKey = "startAt", endKey = "endAt", isDate = false } = opts;
    const oldStart = prev[startKey];
    const oldEnd = prev[endKey];
    if (!newStart || !oldStart || !oldEnd) {
        return { ...prev, [startKey]: newStart };
    }
    const oldStartObj = dayjs(oldStart);
    const oldEndObj = dayjs(oldEnd);
    if (!oldStartObj.isValid() || !oldEndObj.isValid()) {
        return { ...prev, [startKey]: newStart };
    }
    const deltaMs = oldEndObj.valueOf() - oldStartObj.valueOf();
    const newEndObj = dayjs(newStart).add(deltaMs, "millisecond");
    const fmt = isDate ? "YYYY-MM-DD" : "YYYY-MM-DDTHH:mm";
    return { ...prev, [startKey]: newStart, [endKey]: newEndObj.format(fmt) };
};

/**
 * 일정 종료 변경 시 form 상태 업데이트.
 *
 * <p>새 종료가 시작보다 앞이면 시작도 종료로 함께 보정해 broken state 방지.
 * 그 외엔 종료만 set.
 *
 * <p>lexicographic 비교 사용 — "YYYY-MM-DD" / "YYYY-MM-DDTHH:mm" 같은 ISO 8601 prefix 형식에서 안전.
 */
export const applyRangeEndChange = (prev, newEnd, opts = {}) => {
    const { startKey = "startAt", endKey = "endAt" } = opts;
    const oldStart = prev[startKey];
    if (!newEnd || !oldStart) {
        return { ...prev, [endKey]: newEnd };
    }
    return newEnd < oldStart
        ? { ...prev, [startKey]: newEnd, [endKey]: newEnd }
        : { ...prev, [endKey]: newEnd };
};
